import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ChevronDown, Trophy } from "lucide-react";
import { teamInfo } from "../../data/arsenalData";

// Video libre (Pexels); el enlace anterior de Pixabay devolvía 404
const HERO_VIDEO =
  "https://videos.pexels.com/video-files/10246153/10246153-hd_1920_1080_30fps.mp4";
const HERO_POSTER =
  "https://images.unsplash.com/photo-1777643155941-088f4b2a8940?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHw0fHxzb2NjZXIlMjBtYXRjaCUyMHN0YWRpdW0lMjBuaWdodHxlbnwwfHx8fDE3NzgwMzg4ODd8MA&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-[#061930]"
    >
      {/* Parallax video background */}
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <video
          autoPlay
          muted
          loop
          playsInline
          poster={HERO_POSTER}
          className="w-full h-full object-cover"
          data-testid="hero-video"
        >
          <source src={HERO_VIDEO} type="video/mp4" />
        </video>
        {/* Dark cinematic overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#061930]/80 via-[#061930]/50 to-[#061930]" />
        <div className="absolute inset-0 bg-[#061930]/40" />
        {/* Red vignette */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(219,0,7,0.25),transparent_60%)]" />
      </motion.div>

      {/* Crossed stripes decoration */}
      <div className="absolute inset-0 z-[1] pointer-events-none">
        <div className="absolute top-0 bottom-0 left-[15%] w-px bg-gradient-to-b from-transparent via-white/10 to-transparent" />
        <div className="absolute top-0 bottom-0 right-[15%] w-px bg-gradient-to-b from-transparent via-white/10 to-transparent" />
      </div>

      {/* Content: flujo vertical para que la franja Años/Trofeos no se superponga a #matchday */}
      <motion.div
        style={{ opacity }}
        className="relative z-10 flex min-h-screen flex-col max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-24 pb-32"
      >
        <div className="relative flex flex-1 flex-col justify-center py-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex items-center gap-3 mb-8"
          >
            <div className="h-px w-12 bg-[#9C824A]" />
            <span className="font-outfit text-[#9C824A] text-xs font-semibold uppercase tracking-[0.35em]">
              Fundado 2012 · Allende, N.L. · Fútbol 7
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="font-anton uppercase text-white hero-text-shadow leading-[0.85] tracking-tight"
            style={{ fontSize: "clamp(3rem, 11vw, 10rem)" }}
            data-testid="hero-headline"
          >
            Más que equipo,
            <br />
            <span className="text-[#DB0007]">una familia.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-8 max-w-2xl font-outfit text-base sm:text-lg text-white/80 leading-relaxed"
          >
            Desde 2012, Arsenal de Allende lleva la pasión del fútbol a cada rincón
            de Nuevo León. Hermandad, disciplina y corazón en cada partido.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a href="#matchday" data-testid="hero-cta-primary" className="btn-arsenal">
              <span>Próximo Partido</span>
            </a>
            <a href="#historia" data-testid="hero-cta-secondary" className="btn-outline-gold">
              <Trophy size={18} />
              <span>Nuestra Historia</span>
            </a>
          </motion.div>

          {/* Right-side emblem */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.6 }}
            className="hidden lg:block pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 z-0 xl:right-4"
          >
            <img
              src={teamInfo.logo}
              alt="Arsenal de Allende escudo"
              className="w-[280px] h-[280px] xl:w-[380px] xl:h-[380px] object-contain opacity-90 drop-shadow-[0_0_60px_rgba(219,0,7,0.4)]"
            />
          </motion.div>
        </div>

        {/* Bottom stats bar — dentro del flujo del documento */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="mt-auto w-full max-w-4xl shrink-0"
        >
          <div className="grid grid-cols-3 gap-px bg-white/10 border border-white/10">
            <StatCell label="Años" value="14" />
            <StatCell label="Trofeos" value="03" highlighted />
            <StatCell label="Jugadores" value="14" />
          </div>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 text-white/60"
      >
        <ChevronDown size={24} />
      </motion.div>
    </section>
  );
};

const StatCell = ({ label, value, highlighted }) => (
  <div className={`p-4 sm:p-6 ${highlighted ? "bg-[#DB0007]" : "bg-[#061930]/70 backdrop-blur-md"}`}>
    <div className={`font-outfit text-[10px] sm:text-xs uppercase tracking-[0.3em] ${highlighted ? "text-white/80" : "text-[#9C824A]"}`}>
      {label}
    </div>
    <div className="font-anton text-3xl sm:text-5xl text-white mt-1">{value}</div>
  </div>
);
